/**
 * ApPage 應付帳款 (S8.1) — Board D #8, 會計 Finance nav.
 *
 * Split out of the codex `FinancePages.tsx` onto the house list stack
 * (useListPage + GenericFilterPopover + TableContainer state triad). Views are
 * ROUTE-DERIVED (`/auth/finance/ap/:view`, view = invoices|payments) —
 * selection lives in the URL, never in component state.
 *
 * - **Pay-only-actual-supply** 財務只需要付實際供給: every AP row carries the
 *   server-computed payable block (min(需求,供給) − 退回 per line; the 上貨
 *   qty never enters the payment basis). The release settles the payable
 *   amount, not the invoice face amount.
 * - **Three-way match + dual-control release ported intact**: PO · 進貨 ·
 *   vendor invoice per line; release needs 3 distinct actors
 *   (製單/核准/放行, preparer must match the recorded 製單人) and a mandatory
 *   reason. Release is HIDDEN unless the match passed (hide-don't-disable).
 * - **沖帳紀錄 stub** (payments view): 付款狀態表-shaped columns
 *   (付款單號/供應商/到期日/幣別/沖帳金額) joining payment ↔ invoice ↔ the
 *   採購單總表 refs, with the 會計科目 placeholder (R217).
 * - The 兆豐/玉山 bank remittance export button is deliberately NOT rendered:
 *   the bank file layouts are unspecified (G6/Judy) — hidden until spec'd,
 *   never a disabled stub.
 *
 * Slice-local seam (S8.1 file-set): api calls + row types are co-located here
 * because `src/hooks/**` / `src/services/api/**` / `src/types/api/finance.ts`
 * belong to concurrent slices — follow-up: lift into `useApInvoices.ts` +
 * `finance.api.ts` types when those files free up.
 */

import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  App,
  Badge,
  Button,
  Col,
  Dropdown,
  Input,
  Modal,
  Row,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  AuditOutlined,
  FilterOutlined,
  MoreOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAntdTable } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { extractApiError } from '@/hooks/useSalesOrders';
import apiClient from '@/services/api/axios.config';
import { clearCacheWhere } from '@/utils/cacheKeyRegistry';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { ApInvoiceStatus, VendorInvoice } from '@/types/api/finance';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

export type ApView = 'invoices' | 'payments';

const AP_VIEWS: readonly ApView[] = ['invoices', 'payments'] as const;

// ---------------------------------------------------------------------------
// Slice-local data seam (mirrors the handler response shapes — see docblock)
// ---------------------------------------------------------------------------

/** Supply facts + derived payables per match line (handler-computed). */
interface ApPayableLine {
  poLineId: string;
  sku: string;
  qtyDemand: number;
  qtySupplied: number;
  qtyLoaded: number;
  qtyReturnedToVendor: number;
  invoicedQty: number;
  unitPrice: number;
  invoicedAmount: number;
  payableQty: number;
  payableAmount: number;
}

interface ApPayableSummary {
  lines: ApPayableLine[];
  payableAmount: number;
  varianceAmount: number;
}

/** AP row = vendor invoice + the server-computed payable block. */
type ApRow = VendorInvoice & { payable: ApPayableSummary };

/** 沖帳 row — 付款狀態表-shaped (see mocks/data/finance ReconciliationEntry). */
interface ReconciliationRow {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  reconciledBy: string;
  vendorName: string;
  docType: 'remittance';
  dueDate: string;
  currency: string;
  faceAmount: number;
  reconciledAmount: number;
  apInvoiceNumber: string;
  poNumbers: string[];
  docketNumbers: string[];
  containerNumbers: string[];
  accountCode: string | null;
}

const apApi = {
  list: async (): Promise<{ data: ApRow[] }> => {
    const { data } = await apiClient.get<{ data: ApRow[] }>('/finance/ap-invoices');
    return data;
  },
  release: async (
    id: string,
    body: { preparer: string; approver: string; releaser: string; reason: string }
  ): Promise<{ data: ApRow }> => {
    const { data } = await apiClient.post<{ data: ApRow }>(
      `/finance/ap-invoices/${id}/dual-control-release`,
      body
    );
    return data;
  },
  reconciliations: async (): Promise<{ data: ReconciliationRow[] }> => {
    const { data } = await apiClient.get<{ data: ReconciliationRow[] }>(
      '/finance/reconciliations'
    );
    return data;
  },
};

/** An AP release also moves the 沖帳 list — invalidate the finance caches. */
function invalidateApCaches() {
  clearCacheWhere((key) => key.startsWith('/api/finance'));
}

const AP_STATUSES: readonly ApInvoiceStatus[] = [
  'draft',
  'matched',
  'pending_approval',
  'approved',
  'paid',
  'held',
] as const;

/** antd Tag presets per status (house precedent; no colour literals). */
const AP_STATUS_COLOR: Record<ApInvoiceStatus, string> = {
  draft: 'default',
  matched: 'blue',
  pending_approval: 'orange',
  approved: 'cyan',
  paid: 'green',
  held: 'red',
};

const THREE_WAY_COLOR: Record<'pass' | 'fail' | 'pending', string> = {
  pass: 'green',
  fail: 'red',
  pending: 'orange',
};

/** Release available only when the match passed and nothing was paid yet. */
function isReleasable(row: ApRow): boolean {
  return (
    row.threeWayMatch === 'pass' &&
    !row.paidAt &&
    (row.status === 'matched' || row.status === 'pending_approval' || row.status === 'approved')
  );
}

// ---------------------------------------------------------------------------
// Invoices view — three-way match + dual-control release
// ---------------------------------------------------------------------------

interface ApFilters {
  search: string;
  status: string;
  [key: string]: unknown;
}

const defaultApFilters = (): ApFilters => ({ search: '', status: 'all' });

function useApInvoicesTable(filters: ApFilters): TableHookResult<ApRow> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const rows = (await apApi.list()).data;
      const term = filters.search.trim().toLowerCase();
      const filtered = rows.filter((row) => {
        if (filters.status !== 'all' && row.status !== filters.status) return false;
        if (!term) return true;
        return [row.invoiceNumber, row.vendorName, row.poNumber ?? '']
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      return { total: filtered.length, list: filtered.slice(start, start + pageSize) };
    },
    { defaultPageSize: 10, refreshDeps: [filters.search, filters.status] }
  );
  return { tableProps, isLoading: loading || params.length === 0, error: error ?? null, refresh };
}

const releaseSchema = z.object({
  preparer: z.string().trim().min(1, 'finance:ap.release.validation.preparerRequired'),
  approver: z.string().trim().min(1, 'finance:ap.release.validation.approverRequired'),
  releaser: z.string().trim().min(1, 'finance:ap.release.validation.releaserRequired'),
  reason: z.string().trim().min(1, 'finance:ap.release.validation.reasonRequired'),
});

type ReleaseFormData = z.infer<typeof releaseSchema>;

const defaultReleaseValues: ReleaseFormData = {
  preparer: '',
  approver: '',
  releaser: '',
  reason: '',
};

interface ApFilterPopoverValues extends FilterValues {
  status?: string;
}

const ApInvoicesView = () => {
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const [filters, setFilters] = useState<ApFilters>(defaultApFilters);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [matchRow, setMatchRow] = useState<ApRow | null>(null);
  const [releaseRow, setReleaseRow] = useState<ApRow | null>(null);

  const customFilterCounters = useMemo(
    () => ({ status: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    ApFilters,
    ApRow
  >({
    useTableHook: useApInvoicesTable,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReleaseFormData>({
    resolver: zodResolver(releaseSchema),
    defaultValues: defaultReleaseValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const openRelease = (row: ApRow) => {
    setReleaseRow(row);
    // Preparer prefills from the recorded 製單人 — the server re-checks it.
    reset({ ...defaultReleaseValues, preparer: row.preparedBy });
  };

  const closeRelease = () => {
    setReleaseRow(null);
    reset(defaultReleaseValues);
  };

  const localizedReleaseError = (body: { error?: string; message?: string }): string => {
    switch (body.error) {
      case 'ACTORS_NOT_DISTINCT':
        return t('finance:ap.release.errors.actorsNotDistinct');
      case 'PREPARER_MISMATCH':
        return t('finance:ap.release.errors.preparerMismatch');
      case 'MATCH_NOT_PASSED':
        return t('finance:ap.release.errors.matchNotPassed');
      case 'REASON_REQUIRED':
        return t('finance:ap.release.errors.reasonRequired');
      case 'ALREADY_PAID':
        return t('finance:ap.release.errors.alreadyPaid');
      default:
        return body.message || t('common:messages.updateFailed');
    }
  };

  const onRelease = async (values: ReleaseFormData) => {
    if (!releaseRow) return;
    try {
      await apApi.release(releaseRow.id, {
        preparer: values.preparer.trim(),
        approver: values.approver.trim(),
        releaser: values.releaser.trim(),
        reason: values.reason.trim(),
      });
      invalidateApCaches();
      message.success(t('finance:ap.messages.released'));
      closeRelease();
      refresh();
    } catch (error) {
      message.error(localizedReleaseError(extractApiError(error)));
    }
  };

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('finance:ap.fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('finance:ap.filters.allStatuses') },
          ...AP_STATUSES.map((status) => ({
            value: status,
            label: t(`finance:invoices.statusVendor.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const fieldError = (key: keyof ReleaseFormData) =>
    errors[key] ? <Text style={formStyles.errorStyle}>{t(errors[key]?.message ?? '')}</Text> : null;

  const columns: TableProps<ApRow>['columns'] = [
    {
      title: t('finance:ap.fields.invoiceNumber'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 180,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.invoiceNumber} searchTerm={filters.search} fallback="-" />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.invoiceDate}
          </span>
        </div>
      ),
    },
    {
      title: t('finance:ap.fields.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 220,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title: t('finance:ap.fields.purchaseOrder'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 150,
      render: (value: string | null) =>
        value ? <HighlightText text={value} searchTerm={filters.search} fallback="-" /> : renderDash(),
    },
    {
      title: t('finance:ap.fields.invoicedAmount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.amount)}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title={t('finance:ap.payableHint')}>
          <span>{t('finance:ap.fields.payableAmount')}</span>
        </Tooltip>
      ),
      key: 'payableAmount',
      width: 150,
      align: 'right',
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5 tw-items-end">
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: token.fontWeightStrong,
              color: token.colorPrimary,
            }}
          >
            {formatMoney(record.currency, record.payable.payableAmount)}
          </span>
          {record.payable.varianceAmount !== 0 && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorError }}>
              {t('finance:ap.fields.variance')}{' '}
              {formatMoney(record.currency, record.payable.varianceAmount)}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('finance:ap.fields.threeWay'),
      dataIndex: 'threeWayMatch',
      key: 'threeWayMatch',
      width: 120,
      render: (value: ApRow['threeWayMatch']) => (
        <Tag color={THREE_WAY_COLOR[value]} style={{ margin: 0 }}>
          {t(`finance:invoices.threeWay.${value}`)}
        </Tag>
      ),
    },
    {
      title: t('finance:ap.fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 130,
      render: (value: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{value || renderDash()}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.paymentTerms}
          </span>
        </div>
      ),
    },
    {
      title: t('finance:ap.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ApInvoiceStatus) => (
        <Tag color={AP_STATUS_COLOR[status]} style={{ margin: 0 }}>
          {t(`finance:invoices.statusVendor.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('finance:ap.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'match',
                label: t('finance:ap.actions.viewMatch'),
                icon: <AuditOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  setMatchRow(record);
                },
              },
              // Release is HIDDEN unless the match passed (hide-don't-disable)
              ...(isReleasable(record)
                ? [
                    {
                      key: 'release',
                      label: t('finance:ap.actions.release'),
                      icon: <SafetyCertificateOutlined />,
                      onClick: (e: { domEvent: { stopPropagation: () => void } }) => {
                        e.domEvent.stopPropagation();
                        openRelease(record);
                      },
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('finance:ap.actions.actionsFor', {
              invoiceNumber: record.invoiceNumber,
            })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  const releasePayable = releaseRow?.payable.payableAmount ?? 0;

  return (
    <>
      {/* Search + status filter toolbar.
          兆豐/玉山 bank remittance export button intentionally NOT rendered —
          field layouts unspecified (G6/Judy); hidden until spec'd. */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('finance:ap.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<ApFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) =>
            setFilters((prev) => ({ ...prev, status: (values.status as string) || 'all' }))
          }
          initialFilters={{ status: filters.status }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
      </div>

      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('finance:ap.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ApRow>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => setMatchRow(record),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('finance:ap.states.noResults')}
                actionLabel={t('finance:ap.states.clearFilters')}
                onAction={() => setFilters(defaultApFilters())}
                actionType="default"
              />
            ) : (
              <ListEmptyState title={t('finance:ap.states.noInvoices')} />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1290 }}
        />
      </TableContainer>

      {/* 三方核對明細 — PO · 進貨 · 供應商發票 with the supply facts */}
      <Modal
        open={!!matchRow}
        title={t('finance:ap.matchModal.title')}
        onCancel={() => setMatchRow(null)}
        width={960}
        footer={[
          <Button
            key="close"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => setMatchRow(null)}
          >
            {t('common:actions.close')}
          </Button>,
        ]}
      >
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
        >
          {t('finance:ap.payableHint')}
        </Text>
        <Table<ApPayableLine>
          size="small"
          rowKey="poLineId"
          dataSource={matchRow?.payable.lines ?? []}
          pagination={false}
          scroll={{ x: 900 }}
          columns={[
            { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 150 },
            {
              title: t('finance:ap.matchModal.qtyDemand'),
              dataIndex: 'qtyDemand',
              key: 'qtyDemand',
              align: 'right',
            },
            {
              title: t('finance:ap.matchModal.qtySupplied'),
              dataIndex: 'qtySupplied',
              key: 'qtySupplied',
              align: 'right',
            },
            {
              title: t('finance:ap.matchModal.qtyLoaded'),
              dataIndex: 'qtyLoaded',
              key: 'qtyLoaded',
              align: 'right',
            },
            {
              title: t('finance:ap.matchModal.qtyReturned'),
              dataIndex: 'qtyReturnedToVendor',
              key: 'qtyReturnedToVendor',
              align: 'right',
            },
            {
              title: t('finance:ap.matchModal.payableQty'),
              dataIndex: 'payableQty',
              key: 'payableQty',
              align: 'right',
              render: (value: number) => (
                <span style={{ fontWeight: token.fontWeightStrong, color: token.colorPrimary }}>
                  {value}
                </span>
              ),
            },
            {
              title: t('finance:ap.matchModal.unitPrice'),
              dataIndex: 'unitPrice',
              key: 'unitPrice',
              align: 'right',
              render: (value: number) =>
                matchRow ? formatMoney(matchRow.currency, value) : renderDash(),
            },
            {
              title: t('finance:ap.matchModal.invoicedAmount'),
              dataIndex: 'invoicedAmount',
              key: 'invoicedAmount',
              align: 'right',
              render: (value: number) =>
                matchRow ? formatMoney(matchRow.currency, value) : renderDash(),
            },
            {
              title: t('finance:ap.matchModal.payableAmount'),
              dataIndex: 'payableAmount',
              key: 'payableAmount',
              align: 'right',
              render: (value: number) => (
                <span style={{ fontWeight: token.fontWeightStrong, color: token.colorPrimary }}>
                  {matchRow ? formatMoney(matchRow.currency, value) : renderDash()}
                </span>
              ),
            },
          ]}
        />
      </Modal>

      {/* 雙人覆核放行 — 3 distinct actors + mandatory reason */}
      <Modal
        open={!!releaseRow}
        title={t('finance:ap.release.title')}
        onCancel={closeRelease}
        width={640}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeRelease}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="release"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            loading={isSubmitting}
            onClick={() => void handleSubmit(onRelease)()}
          >
            {t('finance:ap.release.submit')}
          </Button>,
        ]}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
          {t('finance:ap.release.description')}
        </Text>
        {releaseRow && (
          <Text strong style={{ display: 'block', marginBottom: token.marginMD }}>
            {t('finance:ap.release.payingLabel')}
            {': '}
            <span style={{ fontVariantNumeric: 'tabular-nums', color: token.colorPrimary }}>
              {formatMoney(releaseRow.currency, releasePayable)}
            </span>
          </Text>
        )}
        <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:ap.release.preparer')}
              </Text>
              <Controller
                name="preparer"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={errors.preparer ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('preparer')}
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:ap.release.approver')}
              </Text>
              <Controller
                name="approver"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={errors.approver ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('approver')}
            </Col>
            <Col span={8}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:ap.release.releaser')}
              </Text>
              <Controller
                name="releaser"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={errors.releaser ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('releaser')}
            </Col>
          </Row>
          <Row gutter={[16, 16]} style={{ marginTop: '12px' }}>
            <Col span={24}>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('finance:ap.release.reason')}
              </Text>
              <Controller
                name="reason"
                control={control}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    rows={3}
                    style={{ ...formStyles.inputStyle, height: 'auto' }}
                    status={errors.reason ? 'error' : undefined}
                  />
                )}
              />
              {fieldError('reason')}
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

// ---------------------------------------------------------------------------
// Payments view — 沖帳紀錄 (付款狀態表-shaped stub)
// ---------------------------------------------------------------------------

interface PaymentFilters {
  search: string;
  [key: string]: unknown;
}

function useReconciliationsTable(filters: PaymentFilters): TableHookResult<ReconciliationRow> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const rows = (await apApi.reconciliations()).data;
      const term = filters.search.trim().toLowerCase();
      const filtered = rows.filter((row) => {
        if (!term) return true;
        return [
          row.paymentNumber,
          row.vendorName,
          row.apInvoiceNumber,
          row.poNumbers.join(' '),
          row.containerNumbers.join(' '),
          row.docketNumbers.join(' '),
        ]
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      return { total: filtered.length, list: filtered.slice(start, start + pageSize) };
    },
    { defaultPageSize: 10, refreshDeps: [filters.search] }
  );
  return { tableProps, isLoading: loading || params.length === 0, error: error ?? null, refresh };
}

const ApPaymentsView = () => {
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();
  const [filters, setFilters] = useState<PaymentFilters>({ search: '' });

  const { tableProps, isLoading, refresh, isFiltered, listState } = useListPage<
    PaymentFilters,
    ReconciliationRow
  >({
    useTableHook: useReconciliationsTable,
    filters,
    filterCountExclusions: ['search'],
  });

  const columns: TableProps<ReconciliationRow>['columns'] = [
    {
      title: t('finance:ap.payments.fields.paymentNumber'),
      dataIndex: 'paymentNumber',
      key: 'paymentNumber',
      width: 160,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.paymentNumber} searchTerm={filters.search} fallback="-" />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.paymentDate}
          </span>
        </div>
      ),
    },
    {
      title: t('finance:ap.payments.fields.reconciledBy'),
      dataIndex: 'reconciledBy',
      key: 'reconciledBy',
      width: 140,
    },
    {
      title: t('finance:ap.payments.fields.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 220,
      render: (value: string) => (
        <HighlightText text={value} searchTerm={filters.search} fallback="-" />
      ),
    },
    {
      title: t('finance:ap.payments.fields.docType'),
      dataIndex: 'docType',
      key: 'docType',
      width: 110,
      render: () => <Tag style={{ margin: 0 }}>{t('finance:ap.payments.docTypeRemittance')}</Tag>,
    },
    {
      title: t('finance:ap.payments.fields.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (value: string) => value || renderDash(),
    },
    {
      title: t('finance:ap.payments.fields.currency'),
      dataIndex: 'currency',
      key: 'currency',
      width: 90,
    },
    {
      title: t('finance:ap.payments.fields.faceAmount'),
      dataIndex: 'faceAmount',
      key: 'faceAmount',
      width: 140,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.faceAmount)}
        </span>
      ),
    },
    {
      title: t('finance:ap.payments.fields.reconciledAmount'),
      dataIndex: 'reconciledAmount',
      key: 'reconciledAmount',
      width: 140,
      align: 'right',
      render: (_: number, record) => (
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: token.fontWeightStrong,
            color: token.colorPrimary,
          }}
        >
          {formatMoney(record.currency, record.reconciledAmount)}
        </span>
      ),
    },
    {
      // 沖帳單號對應採購單總表 — the join refs (invoice × 採購單號 × 貨櫃NO ×
      // 貨單編號); 櫃號 may span multiple records (open with Joe).
      title: t('finance:ap.payments.fields.links'),
      key: 'links',
      width: 240,
      render: (_: unknown, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontSize: token.fontSizeSM }}>
            {t('finance:ap.payments.linkInvoice')}
            {': '}
            <HighlightText
              text={record.apInvoiceNumber}
              searchTerm={filters.search}
              fallback="-"
            />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {t('finance:ap.payments.linkPo')}
            {': '}
            {record.poNumbers.length ? record.poNumbers.join(', ') : '-'}
            {' · '}
            {t('finance:ap.payments.linkContainer')}
            {': '}
            {record.containerNumbers.length ? record.containerNumbers.join(', ') : '-'}
            {' · '}
            {t('finance:ap.payments.linkDocket')}
            {': '}
            {record.docketNumbers.length ? record.docketNumbers.join(', ') : '-'}
          </span>
        </div>
      ),
    },
    {
      title: t('finance:ap.payments.fields.account'),
      dataIndex: 'accountCode',
      key: 'accountCode',
      width: 120,
      render: (value: string | null) =>
        value ?? (
          <Tooltip title={t('finance:ap.payments.accountPendingHint')}>
            <Tag style={{ margin: 0 }}>{t('finance:ap.payments.accountPending')}</Tag>
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('finance:ap.payments.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilters({ search: e.target.value })}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
      </div>

      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('finance:ap.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ReconciliationRow>
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('finance:ap.states.noResults')}
                actionLabel={t('finance:ap.states.clearFilters')}
                onAction={() => setFilters({ search: '' })}
                actionType="default"
              />
            ) : (
              <ListEmptyState title={t('finance:ap.payments.states.noRows')} />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1480 }}
        />
      </TableContainer>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page — route-derived view tabs
// ---------------------------------------------------------------------------

const ApPage = () => {
  const navigate = useNavigate();
  const { view: viewParam } = useParams<{ view: string }>();
  const { t } = useTranslation(['finance', 'common']);

  // Route-derived view — each view component owns its own filter state.
  const view = AP_VIEWS.find((candidate) => candidate === viewParam) ?? null;

  if (!view) {
    return <Navigate to="/auth/finance/ap/invoices" replace />;
  }

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('finance:ap.title')} />
      <Tabs
        activeKey={view}
        onChange={(key) => navigate(`/auth/finance/ap/${key}`)}
        items={[
          { key: 'invoices', label: t('finance:ap.tabs.invoices') },
          { key: 'payments', label: t('finance:ap.tabs.payments') },
        ]}
      />
      {view === 'invoices' ? <ApInvoicesView /> : <ApPaymentsView />}
    </div>
  );
};

export default ApPage;
